import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import "./App.scss";
import { Navbar } from "./components/Navbar/Navbar";
import { LoginPage } from "./pages/LoginPage";
import ResumeCareerSetupPage from "./pages/ResumeCareerSetupPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import InterviewHistoryPage from "./pages/InterviewHistoryPage";
import InterviewSeriesDetailsPage from "./pages/InterviewSeriesDetailsPage";
import InterviewFeedbackPage from "./pages/InterviewFeedbackPage";
import SettingsPage from "./pages/SettingsPage";
import CalendarCallbackPage from "./pages/CalendarCallbackPage";
import { ToastProvider, useToast } from "./components/common/Toast";
import { AuthProvider } from "./contexts/AuthContext";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CancellationRefundPolicy from "./pages/CancellationRefundPolicy";
import DashboardPage from "./pages/DashboardPage";
import DashboardNew from "./pages/DashboardNew";
import ScrollToTop from "./utils/ScrollToTop";
import { ModalProvider } from "./components/common/ModalPortal";
import ModalContextProvider from "./contexts/ModalContext";
import { ResponsiveProvider } from './contexts/ResponsiveContext';
import { ThemeProvider } from './components/common/ThemeProvider';
import AnalyticsProvider from './contexts/AnalyticsContext';
import useAuthStoreInitializer from './hooks/useAuthStoreInitializer';
import { useAuthStore } from './store';
import { AUTH_FAILURE_EVENT } from './services/apiClient';
import authService from './services/authService';
import careerService from './services/careerService';
import subscriptionService from './services/subscriptionService';
import InterviewPreviewPage from "./pages/InterviewPreviewPage";
import ScreeningInterviewPage from "./pages/ScreeningInterviewPage";
import PublicInterviewPage from "./pages/PublicInterviewPage";
import PublicInterviewActivePage from "./pages/PublicInterviewActivePage";
import StableInterviewPage from './modules/interview/pages/InterviewPage';
import getMockFeedback from "./mock/interviewFeedback";
import ConnectionMonitor from './components/common/ConnectionMonitor';
import ConnectionStabilityIndicator from './components/common/ConnectionStabilityIndicator';
import DomainUpdateDetailsPage from './pages/DomainUpdateDetailsPage';
import { InterviewDetails, InterviewFeedback, Message } from "./types/interviews";
import { InterviewGoal } from "./services/configService";
import interviewService from "./services/interviewService";
import { sanitizeForNavigation } from './utils/navigationUtils';
import PaymentResultPage from "./pages/PaymentResultPage";
import { FeedPage } from './modules/feed';
import BrowseSeriesPage from './pages/BrowseSeriesPage';
import { 
  trackPageView, 
  initializeUTMTracking, 
  initializeMicrosoftClarity,
  setClarityTag 
} from './firebaseConfig';
import { useWeekendDetection } from "./hooks/useWeekendDetection";
      // import FeedbackService from './services/feedbackService';
(function() {
  // Get theme from localStorage
  const savedTheme = localStorage.getItem('theme');
  
  // Force correct theme application..
  if (savedTheme === 'light') {
    // For light theme, REMOVE dark class
    document.documentElement.classList.remove('dark');
  } else if (savedTheme === 'dark') {
    // For dark theme, ADD dark class
    document.documentElement.classList.add('dark');
  } else {
    // Default to light (change this if your default should be dark)
    document.documentElement.classList.remove('dark');
  }
  
  // Set up a mutation observer to track any class changes
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'class') {
        console.log("[Theme Observer] Stack trace:", new Error().stack);
      }
    });
  });
  
  // Start observing
  observer.observe(document.documentElement, { attributes: true });
  
  // Enforce theme again after a short delay to catch any post-initialization changes
  setTimeout(() => {
    if (savedTheme === 'light' && document.documentElement.classList.contains('dark')) {
      console.log("[Delayed Theme Fix] Detected incorrect 'dark' class, removing it");
      document.documentElement.classList.remove('dark');
    } else if (savedTheme === 'dark' && !document.documentElement.classList.contains('dark')) {
      console.log("[Delayed Theme Fix] Dark theme missing 'dark' class, adding it");
      document.documentElement.classList.add('dark');
    }
  }, 100);
})();

// Define application states
type AppState = 'auth' | 'setup-profile' | 'subscriptions';

// Create a memoized ProtectedRoute component to prevent unnecessary re-renders
const ProtectedRoute = React.memo(({ 
  children, 
  isAuthenticated
}: { 
  children: React.ReactNode;
  isAuthenticated: boolean;
}) => {
  const location = useLocation();
  // Check if user has been explicitly logged out
  const isExplicitlyLoggedOut = localStorage.getItem('mocktrail-auth-storage') === null &&
                               localStorage.getItem('mocktrail-token') === null;
  
  // Check localStorage as a fallback authentication source
  const hasLocalAuthFlag = localStorage.getItem('isAuthenticated') === 'true';
  const hasToken = localStorage.getItem('mocktrail-token') !== null;
  const effectiveIsAuthenticated = isAuthenticated || (hasLocalAuthFlag && hasToken);
  
  console.log("Protected route check for path:", location.pathname);
  console.log("Is authenticated from store:", isAuthenticated);
  
  // If not authenticated or logged out, redirect to login
  if (!effectiveIsAuthenticated || isExplicitlyLoggedOut) {
    console.log("Not authenticated, redirecting to login");
    return <Navigate to="/login" />;
  }
  
  console.log("Authentication passed, rendering children");
  return <>{children}</>;
});

// Give it a display name for better debugging
ProtectedRoute.displayName = 'ProtectedRoute';

function MainApp() {
  const navigate = useNavigate();
  const location = useLocation();
  useAuthStoreInitializer();
  
  // Get user data from auth store instead of using mock data
  const { user, isAuthenticated: storeIsAuthenticated, logout: storeLogout, updateUser } = useAuthStore();

  // Check for toast flags
  const { showToast } = useToast();
  
  const { isWeekend } = useWeekendDetection();
  // Check if we should show the banner based on current route
   const shouldShowBanner = isWeekend && (location.pathname === '/' || location.pathname === '/dashboard'
    || location.pathname === '/my-interviews' || location.pathname === '/browse-series' || location.pathname === '/feed'
   );
  // Initialize analytics on component mount
  useEffect(() => {
    // Initialize UTM tracking
    initializeUTMTracking();
    
    // Initialize Microsoft Clarity
    initializeMicrosoftClarity();
    
    console.log('Analytics initialized for MockTrail Frontend');
  }, []);

  // Track page views when location changes
  useEffect(() => {
    const pageName = location.pathname === '/' ? 'Home' : 
                    location.pathname.replace('/', '').replace(/\//g, ' - ') || 'Unknown Page';
    
    trackPageView(pageName, {
      full_path: location.pathname + location.search,
      user_authenticated: storeIsAuthenticated
    });
  }, [location.pathname, location.search, storeIsAuthenticated]);

  // Set Clarity user tags when authentication state changes
  useEffect(() => {
    if (storeIsAuthenticated && user) {
      setClarityTag('user_authenticated', 'true');
      setClarityTag('user_id', user._id || 'unknown');
      if (user.subscriptionPlan) {
        setClarityTag('subscription_plan', user.subscriptionPlan);
      }
    } else {
      setClarityTag('user_authenticated', 'false');
    }
  }, [storeIsAuthenticated, user]);

  // Subscription data state
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    planType: 'free' | 'monthly' | 'yearly' | 'quarterly' | 'quarterly';
    planName: string;
    isActive: boolean;
    isRecurring: boolean;
    expiryDate: string;
    minutesLeft: number;
    isUnlimited?: boolean;
  }>({
    planType: 'free',
    planName: 'Free Plan',
    isActive: false,
    isRecurring: false,
    expiryDate: '',
    minutesLeft: 0,
    isUnlimited: false
  });
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

  // Fetch subscription info
  useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      if (!storeIsAuthenticated) return;
      
      try {
        setIsLoadingSubscription(true);
        const info = await subscriptionService.getSubscriptionInfo();
        console.log('App: Fetched subscription info:', info);
        setSubscriptionInfo(info);
      } catch (error) {
        console.error('App: Error fetching subscription info:', error);
      } finally {
        setIsLoadingSubscription(false);
      }
    };
    
    fetchSubscriptionInfo();
  }, [storeIsAuthenticated]);
  
  // Function to refresh subscription info from child components
  const refreshSubscriptionInfo = async () => {
    try {
      setIsLoadingSubscription(true);
      console.log('App: Refreshing subscription info...');
      const info = await subscriptionService.getSubscriptionInfo();
      console.log('App: Refreshed subscription info:', info);
      setSubscriptionInfo(info);
    } catch (error) {
      console.error('App: Error refreshing subscription info:', error);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  // this video reference is used for displaying the active stream, whether that is the webcam or screen capture
  // feel free to style as you see fit
  const videoRef = useRef<HTMLVideoElement>(null);
  // either the screen capture, the video or null, if null we hide it
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);

  // Track application state
  const [appState, setAppState] = useState<AppState>('auth');
  // Loading state for profile API
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(false);
  // Track if initial profile fetch has been attempted
  const [profileFetchAttempted, setProfileFetchAttempted] = useState<boolean>(false);

  // Determine if we should show the navbar - not on login page or interview page
  const shouldShowNavbar = useMemo(() => {
    return storeIsAuthenticated &&
           location.pathname !== '/login' &&
           !location.pathname.startsWith('/interview/') &&
           !location.pathname.startsWith('/screening/') &&
           !location.pathname.startsWith('/screening-interview/') &&
           !location.pathname.startsWith('/screening-interview-active/');
  }, [storeIsAuthenticated, location.pathname]);
  
  // Fetch user profile when app loads and user is authenticated
  useEffect(() => {
    const fetchUserProfile = async () => {
      // Skip if:
      // 1. Not authenticated
      // 2. Already attempted to fetch profile
      // 3. Loading profile is in progress
      // 4. User data is already available with critical fields (it was fetched by useAuthStoreInitializer)
      if (!storeIsAuthenticated || 
          profileFetchAttempted || 
          isLoadingProfile || 
          (user && user._id && user.email)) {
        // If we have user data from auth store, no need to fetch again
        if (user && user._id && user.email) {
          console.log("Skipping profile fetch, user data already available in auth store:", user._id);
          setProfileFetchAttempted(true);
        }
        return;
      }

      try {
        setIsLoadingProfile(true);
        console.log("App: Checking if user profile needs to be fetched...");
        
        // Check for cached user data in authService first to avoid duplicate API calls
        const cachedUser = authService.getCachedUserData();
        if (cachedUser && cachedUser._id && cachedUser.email) {
          console.log("App: Using cached user data from authService:", cachedUser._id);
          
          // Update the auth store with the cached profile data
          updateUser({
            ...user,
            _id: cachedUser._id,
            email: cachedUser.email,
            name: cachedUser.name,
            profilePicture: cachedUser.profilePicture,
            // Copy other fields if available
            subscriptionPlan: cachedUser.subscriptionPlan,
            subscriptionExpiry: cachedUser.subscriptionExpiry,
            remainingMinutes: cachedUser.remainingMinutes,
            activePlan: cachedUser.activePlan
          });
          console.log("App: User profile updated in auth store from cache");
          setProfileFetchAttempted(true);
          return;
        }
        
        // Log auth status before API call
        const token = localStorage.getItem('mocktrail-token');
        console.log("App: Auth token available:", !!token);
        if (token) {
          console.log("App: Token prefix:", token.substring(0, 15) + "...");
        } else {
          console.warn("App: No auth token found in localStorage but user is marked as authenticated");
          // Handle this edge case - may need to refresh token or logout
          if (storeLogout) {
            console.log("App: Logging out due to missing token");
            storeLogout();
            return;
          }
        }
        
        // Instead of directly calling userService, use the store's fetchCurrentUser method
        // which has been updated to respect caching and avoid duplicate calls
        console.log("App: No cached data available, delegating profile fetch to auth store");
        const { fetchCurrentUser } = useAuthStore.getState();
        await fetchCurrentUser(false); // Don't force refresh
        console.log("App: User profile fetch via auth store completed");
        setProfileFetchAttempted(true);
      } catch (error: any) {
        console.error("App: Error fetching user profile:", error);
        console.error("App: Error details:", error.message);
        
        // Extract and log response details if available
        if (error.response) {
          console.error("App: Response status:", error.response.status);
          console.error("App: Response data:", error.response.data);
          
          // Handle 401 Unauthorized errors
          // Note: We don't need to call logout here as the auth failure event will handle it
          if (error.response.status === 401) {
            console.warn("App: Unauthorized error when fetching profile - token may be invalid");
            // Don't call storeLogout here to avoid duplicate logout calls
          }
        }
      } finally {
        setIsLoadingProfile(false);
        setProfileFetchAttempted(true);
      }
    };

    fetchUserProfile();
  }, [storeIsAuthenticated, user, updateUser, profileFetchAttempted, isLoadingProfile, storeLogout]);
  
  // Listen for auth failure events from apiClient
  useEffect(() => {
    // Flag to prevent handling multiple auth failures in quick succession
    let isHandlingAuthFailure = false;
    
    const handleAuthFailure = (event: CustomEvent) => {
      console.warn('Auth failure event received:', event.detail);
      
      // Get the reason for the auth failure
      const reason = event.detail?.reason || 'unknown';
      
      // Only proceed with logout for specific auth failure reasons:
      // - refresh_token_error: Error occurred during token refresh
      // - refresh_token_invalid: Refresh token was rejected
      // - user_logged_out: User is already logged out
      const shouldLogout = [
        'refresh_token_error',
        'refresh_token_invalid',
        'user_logged_out'
      ].includes(reason);
      
      // Skip logout for other types of auth failures which should just trigger a token refresh
      if (!shouldLogout) {
        console.log(`Auth failure '${reason}' doesn't require logout, skipping`);
        return;
      }
      
      // Prevent multiple logout attempts using local flag
      if (isHandlingAuthFailure || !storeIsAuthenticated) {
        console.log('Already handling auth failure or logged out, ignoring event');
        return;
      }
      
      // Set flag to prevent concurrent handling
      isHandlingAuthFailure = true;
      
      // Small delay to ensure any token refresh attempt has completed
      setTimeout(() => {
        // Check if we're still authenticated after the delay
        // This prevents logout if token refresh succeeded in the meantime
        if (storeIsAuthenticated && localStorage.getItem('mocktrail-token')) {
          console.log('Still authenticated after delay, no need to logout');
          isHandlingAuthFailure = false;
          return;
        }
        
        // Call the storeLogout function to properly handle the logout
        console.log('Triggering logout due to auth failure:', reason);
        storeLogout();
        
        // Navigate to login page
        navigate('/login', { replace: true });
        
        // Create a descriptive message based on the failure reason
        let message = 'Your session has expired. Please login again.';
        if (reason === 'refresh_token_error') {
          message = 'We had trouble refreshing your session. Please login again.';
        } else if (reason === 'refresh_token_invalid') {
          message = 'Your session could not be refreshed. Please login again.';
        }
        
        // Display message (you can use a Toast component if available)
        console.warn(message);
        // If you have a toast service:
        // toast.error(message);
        
        // Reset flag
        isHandlingAuthFailure = false;
      }, 300); // Short delay to allow token refresh to complete if in progress
    };
    
    // Add event listener for auth failures
    window.addEventListener(AUTH_FAILURE_EVENT, handleAuthFailure as EventListener);
    
    return () => {
      window.removeEventListener(AUTH_FAILURE_EVENT, handleAuthFailure as EventListener);
    };
  }, [navigate, storeIsAuthenticated, storeLogout]);
  
  // Listen for storage events to detect when another tab logs out or clears auth state
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      console.log("STORAGE EVENT:", event.key, "changed");
      
      // Check if auth related storage was changed
      if (event.key === 'mocktrail-auth-storage' || event.key === 'mocktrail-token' || event.key === 'isAuthenticated') {
        console.log("Auth storage changed, checking auth state consistency");
        
        // Check if auth storage and token exist
        const hasAuthStorage = localStorage.getItem('mocktrail-auth-storage') !== null;
        const hasToken = localStorage.getItem('mocktrail-token') !== null;
        const hasAuthFlag = localStorage.getItem('isAuthenticated') === 'true';
        
        // If storage is missing but we're still authenticated in store, clear auth state
        if ((!hasAuthStorage || !hasToken || !hasAuthFlag) && storeIsAuthenticated) {
          console.log("Detected auth storage was cleared, logging out in this tab");
          
          // Call store logout to sync state
          storeLogout();
          
          // Redirect to login page if not already there
          if (location.pathname !== '/login') {
            console.log("Redirecting to login page due to logout");
            navigate('/login', { replace: true });
          }
        }
      }
    };
    
    // Add event listener for storage events
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate, location.pathname, storeIsAuthenticated, storeLogout]);
  
  // User data prepared for components
  const [safeUser, setSafeUser] = useState<{
    id: string;
    name: string;
    email: string;
    profilePicture: string;
    profileImage: string;
    interviewGoals: string[];
  }>({
    id: "guest",
    name: "-",
    email: "",
    profilePicture: "",
    profileImage: "",
    interviewGoals: []
  });
  
  // Update safe user object whenever user data changes
  useEffect(() => {
    const prepareSafeUser = async () => {
      if (!user) {
        // Fallback user data if auth store user is null
        setSafeUser({
          id: "guest",
          name: "-",
          email: "",
          profilePicture: "",
          profileImage: "",
          interviewGoals: []
        });
        return;
      }
      
      // Log user data to help debug profile picture issues
      console.log("App.tsx - Creating safeUser from:", {
        user,
        hasProfilePicture: !!user.profilePicture,
        profilePictureValue: user.profilePicture,
        subscriptionInfo
      });
      
      try {
        // Fetch interview goals from career service
        const preferences = await careerService.getPreferences();
        
        // Map the User type to the expected format for components
        setSafeUser({
          id: user._id || "guest",
          name: user.name || user.email?.split('@')[0] || "User",
          email: user.email || "",
          profilePicture: user.profilePicture || "",
          profileImage: user.profilePicture || "",
          interviewGoals: preferences?.interviewGoals || []
        });
      } catch (error) {
        console.error("Error preparing user data:", error);
        // Set fallback user data in case of error
        setSafeUser({
          id: user._id || "guest",
          name: user.name || user.email?.split('@')[0] || "User",
          email: user.email || "",
          profilePicture: user.profilePicture || "",
          profileImage: user.profilePicture || "",
          interviewGoals: []
        });
      }
    };
    
    prepareSafeUser();
  }, [user, subscriptionInfo]);
  
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') return 'light';
    
      // Check for saved theme
    const savedTheme = localStorage.getItem('theme');
    
    // Apply saved theme class to document
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (savedTheme === 'light') {
      document.documentElement.classList.remove('dark');
    }
    
    // Check if dark class exists but theme is set to light
    if (savedTheme === 'light' && document.documentElement.classList.contains('dark')) {
      console.log('WARNING: Document has dark class despite light theme in localStorage, correcting...');
      document.documentElement.classList.remove('dark');
    }
    
    // Return saved theme or default to light
    return savedTheme === 'dark' ? 'dark' : 'light';
  });
  
  // Check authentication status
  useEffect(() => {
    // In a real app, we would check for a token or session
    const checkAuth = () => {
      console.log("==== ROUTE DEBUGGING ====");
      console.log("Current location path:", location.pathname);
      
      // Check for direct JobProt signup parameters in URL
      const urlParams = new URLSearchParams(location.search);
      const authCode = urlParams.get('code');
      const source = urlParams.get('source');
      const refCode = urlParams.get('ref');
      const state = urlParams.get('state');
      
      // ENHANCED: Handle direct JobProt signup flow
      if (authCode && source === 'jobprot') {
        console.log("Direct JobProt signup detected in App.tsx");
        console.log("Auth code present:", !!authCode);
        console.log("Referral code present:", !!refCode);
        console.log("State parameter present:", !!state);
        
        // For direct signup, we need to stay on login page to let LoginForm handle the token exchange
        // Don't redirect away even if user appears authenticated from previous session
        if (location.pathname !== '/login' && location.pathname !== '/signup') {
          console.log("Redirecting to login page for direct JobProt signup processing");
          navigate('/login' + location.search, { replace: true }); // Preserve URL parameters
          return;
        }
        
        // If already on login/signup page, let the LoginForm handle the direct signup
        console.log("On login page, letting LoginForm handle direct signup");
        return;
      }
      
      // Check if user has been explicitly logged out
      const isExplicitlyLoggedOut = localStorage.getItem('mocktrail-auth-storage') === null && 
                                   localStorage.getItem('mocktrail-token') === null;
      
      // Check both auth store and localStorage for authentication status
      const hasLocalAuthFlag = localStorage.getItem('isAuthenticated') === 'true';
      const hasToken = localStorage.getItem('mocktrail-token') !== null;
      const effectiveIsAuthenticated = storeIsAuthenticated || (hasLocalAuthFlag && hasToken);
      
      // Check if we have a new user that needs to complete onboarding
      const isNewUser = user?.newUser === true;
      
      console.log("Local storage auth flag:", hasLocalAuthFlag);
      console.log("Has token:", hasToken);
      
      // Handle explicit logout by clearing any remaining auth flags
      if (isExplicitlyLoggedOut && (hasLocalAuthFlag || storeIsAuthenticated)) {
        console.log("Detected inconsistent auth state after logout, cleaning up remaining flags");
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
        // We can't directly call useAuthStore methods here, so just redirect to login
        if (location.pathname !== '/login') {
          console.log("Redirecting to login page due to logout");
          navigate('/login', { replace: true });
        }
        return;
      }
      
      if (effectiveIsAuthenticated && !isExplicitlyLoggedOut) {
        console.log("User is authenticated");
        
        // Only redirect away from login page if the user is NOT a new user or in welcome process
        if (location.pathname === '/login') {
          // Check if we're in the process of showing the welcome form to a new user
          const inWelcomeFormProcess = localStorage.getItem('showing_welcome_form') === 'true';
          
          // Create a stable check for whether to show the welcome form - consider both:
          // 1. User is marked as a new user in their profile
          // 2. We're already in the welcome form process (to prevent disrupting it)

          console.log("App - CheckAuth: isNewUser:", isNewUser);
          console.log("App - CheckAuth: inWelcomeFormProcess:", inWelcomeFormProcess);
          
          const shouldShowWelcomeForm = inWelcomeFormProcess;
          // && !isNewUser;
          
          // Create a stable variable to prevent event dispatch loops
          const hasDispatchedEvent = sessionStorage.getItem('welcome_form_event_dispatched') === 'true';
          
          if (shouldShowWelcomeForm) {
            console.log("New user or welcome form process detected on login page");
            
            // Try to dispatch the event to show the welcome form
            // Ensure the LoginPage component is ready to receive it
            setTimeout(() => {
              console.log("Dispatching showWelcomeForm event");
              const welcomeFormEvent = new CustomEvent('showWelcomeForm');
              window.dispatchEvent(welcomeFormEvent);
              
              console.log("App - CheckAuth: Setting showing_welcome_form flag to true");
              // Set a flag to indicate we're showing the welcome form
              localStorage.setItem('showing_welcome_form', 'true');
              
              // Check if the welcome form is visible after dispatching
              setTimeout(() => {
                const loginComponentElement = document.querySelector('.welcome-form-container');
                if (!loginComponentElement) {
                  console.log("Welcome form not found in DOM, dispatching event again");
                  window.dispatchEvent(welcomeFormEvent);
                } else {
                  console.log("Welcome form found in DOM, dispatch successful");
                }
              }, 500);
            }, 100);
            
            return;
          } else {
            console.log("Redirecting away from login page - regular authenticated user");
            navigate('/', { replace: true });
            return;
          }
        }
        
        // If we're not on the login page anymore, but we have welcome process flags,
        // check if we should still be in the welcome process
        if (location.pathname !== '/login') {
          const inWelcomeFormProcess = localStorage.getItem('showing_welcome_form') === 'true';
          
          // If we navigated away from login but user is a new user and we're in welcome form process,
          // this is likely a navigation error - send them back to the login page
          if (isNewUser && inWelcomeFormProcess && location.pathname !== '/setup-profile') {
            console.log("New user navigated away from login while in welcome process - redirecting back");
            navigate('/login', { replace: true });
            return;
          }
        }
        
        // Track if user is coming from onboarding/subscription page
        if (location.pathname.includes('/onboarding/subscription')) {
          console.log("App: User is in onboarding subscription flow, setting flag");
          localStorage.setItem('from-onboarding', 'true');
          
          // Add another signal in case the primary one fails
          sessionStorage.setItem('show_welcome_interview', 'true');
          
          // Force this flag to persist for testing purposes
          const persistFlagForTesting = false; // Set to true to make flag persist across refreshes
          if (persistFlagForTesting) {
            console.log("App: TESTING MODE - Persisting onboarding flag");
          } else {
            // Set a timer to clear the flag if user doesn't reach dashboard in 10 seconds
            setTimeout(() => {
              if (location.pathname.includes('/onboarding/subscription')) {
                console.log("App: Still on subscription page, preserving flag");
              }
            }, 10000);
          }
        }
        
        // Update app state based on current path
        if (location.pathname === '/setup-profile') {
          console.log("Setting app state to setup-profile");
          setAppState('setup-profile');
        } else if (location.pathname.startsWith('/subscription') || location.pathname.startsWith('/subscriptions')) {
          console.log("Setting app state to subscriptions");
          setAppState('subscriptions');
        } else {
          console.log("Setting app state to selection");
        }
      } else {
        // Define public pages that don't require authentication
        const publicPages = ['/login', '/signup', '/privacy', '/terms', '/cancellation-refund'];
        const publicPaths = ['/screening/', '/screening-interview/', '/screening-interview-active/']; // Paths that start with these are public

        const isPublicPage = publicPages.includes(location.pathname) ||
                            publicPaths.some(path => location.pathname.startsWith(path));

        if (!isPublicPage) {
          console.log("User is not authenticated, redirecting to login");
          // Redirect to login if not authenticated (preserve current URL search params)
          navigate('/login' + location.search);
        } else {
          console.log("User accessing public page, no authentication required");
        }
      }
    };
    
    checkAuth();
  }, [location.pathname, navigate, storeIsAuthenticated, user]);

  // Handle auth completion
  const handleAuthComplete = async () => {
    // Auth is now handled by the auth store so we don't need to manually set isAuthenticated
    // However, let's ensure the isAuthenticated flag is set in localStorage for a more robust solution
    localStorage.setItem('isAuthenticated', 'true');
    
    // Get referral code from localStorage if it exists
    const referralCode = localStorage.getItem('referralCode');
    
    // In a real app, we would send the referral code to the backend here
    if (referralCode) {
      console.log('User signed up with referral code:', referralCode);
      // Here you would make an API call to register the referral
      // api.registerReferral(referralCode);
      
      // Clear the referral code from localStorage after it's been used
      localStorage.removeItem('referralCode');
    }
    
    // Check if the user is a new user in the welcome form process
    const isNewUser = user?.newUser === true;
    const inWelcomeFormProcess = localStorage.getItem('showing_welcome_form') === 'true';
    
    console.log('Auth completion - User state:', { 
      isNewUser,
      inWelcomeFormProcess
    });
    
    if (isNewUser && inWelcomeFormProcess) {
      console.log('New user in welcome form process - staying on login page');
      return; // Stay on login page to complete welcome form
    }

    // For existing users, check if they have resumes and career preferences set up
    if (!isNewUser) {
      console.log('Existing user - checking resume and career preferences');
      try {

        // Check if user has at least one resume
        const resumes = await careerService.getResumes();
        const hasResume = resumes && resumes.length > 0;
        
        // Check if user has career preferences with required fields
        const careerPrefs = await careerService.getPreferences();
        const hasRequiredPrefs = careerPrefs && 
                               careerPrefs.experienceLevel && 
                               careerPrefs.preferredJobRoles.length > 0 &&
                               careerPrefs.interviewGoals.length > 0 &&
                               careerPrefs.languages.length > 0;
        
        console.log('User profile status:', {
          hasResume,
          hasCareerPrefs: !!careerPrefs,
          hasRequiredPrefs
        });
        
        // If user has both resume and required career preferences, navigate to dashboard
        // Resume is made optional, because if user has preferences, that means they setup their profile before.
        if (hasRequiredPrefs) {
          console.log('User has complete profile - navigating to dashboard');
          navigate('/', { replace: true });
          return;
        }
        
        // Otherwise, navigate to setup profile with "login" source parameter
        console.log('User has incomplete profile - navigating to setup profile');
        setAppState('setup-profile');
        navigate('/setup-profile?source=login', { replace: true });
        return;
      } catch (error) {
        console.error('Error checking user profile completeness:', error);
        // In case of error, default to setup profile page
        setAppState('setup-profile');
        navigate('/setup-profile?source=login', { replace: true });
        return;
      }
    }
    
    // For new users who completed welcome form, update app state to profile setup
    setAppState('setup-profile');
    
    // Navigate to setup profile page
    navigate('/setup-profile', { replace: true });
  };

  const handleProfileSetupComplete = () => {
    // After profile setup is complete, move to subscription state with onboarding context
    // CHANGED: Skip subscription page for now, as it's causing user drop;
    setAppState('auth');
    navigate('/')
    localStorage.setItem('from-onboarding', 'true');
    sessionStorage.setItem('show_welcome_interview', 'true');
    // navigate('/onboarding/subscription');
  };

  // Wrap handleInterviewComplete in useCallback
  // Params: interviewId: string, seriesId: string | undefined, feedbackApplicable: boolean, feedbackData?: InterviewFeedback
  const handleInterviewComplete = useCallback(async (_: string, __: string | undefined, feedbackApplicable: boolean, feedbackData?: InterviewFeedback | null) => {
    console.log("App: Interview complete, Feedback data:", feedbackData);
    
    if (!feedbackData || !feedbackApplicable || (feedbackData && feedbackData.interviewDetails === undefined)) {
      console.log("App: No feedback data provided");
      showToast("No Feedback generated as no significant interview activity was detected.", "error");
      navigate('/', { replace: true });
      return;
    }

    console.log("App: About to navigate to feedback page with userInterviewId:", feedbackData?.interviewDetails?.userInterviewId);
    navigate(`/interview-feedback/${feedbackData?.interviewDetails?.userInterviewId}`, { state: { feedbackData } });

    // Update the minutes left by fetching the subscription info from the backend
    // Put 4 seconds delay to ensure the subscription info is updated
    await new Promise(resolve => setTimeout(resolve, 4000));
    const subscriptionInfo = await subscriptionService.getSubscriptionInfo();
    console.log("App Handle Interview Complete: Fetched subscription info after interview completion:", subscriptionInfo);
    setSubscriptionInfo(subscriptionInfo);
  }, [navigate, subscriptionInfo]); // Add subscriptionInfo to dependencies


  const handleThemeToggle = (isDark: boolean) => {
    console.log("Theme toggle event received, isDark:", isDark);
    
    // Save theme preference to localStorage
    const themeValue = isDark ? 'dark' : 'light';
    localStorage.setItem('theme', themeValue);
    console.log(`Saved theme '${themeValue}' to localStorage`);
    
    // Apply theme class to document
    if (isDark) {
      console.log("Applying dark mode - adding 'dark' class");
      document.documentElement.classList.add('dark');
    } else {
      console.log("Applying light mode - removing 'dark' class");
      document.documentElement.classList.remove('dark');
    }
    
    // Update state
    setTheme(themeValue);
  };

  const handleLogoClick = () => {
    // Check if user is authenticated
    if (storeIsAuthenticated) {
      navigate('/');
    } else {
      navigate('/login');
    }
  };
  

  // Handle rendering based on the current path and app state
  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark">
      {/* Show Navbar only if user is authenticated and not on login page */}
      {shouldShowNavbar && <Navbar 
        user={{
          name: safeUser.name,
          email: safeUser.email,
          profilePicture: safeUser.profilePicture,
          isRecurring: subscriptionInfo.isRecurring,
          subscriptionExpiry: subscriptionInfo.expiryDate,
          remainingMinutes: subscriptionInfo.minutesLeft,
          subscriptionPlan: subscriptionInfo.planType
        }}
        onLogoClick={handleLogoClick} 
        subscriptionInfo={subscriptionInfo}
        isLoadingSubscription={isLoadingSubscription}
        shouldShowBanner={shouldShowBanner}
      />}
      {shouldShowBanner && shouldShowNavbar && <div className="h-16 md:h-14"></div>}
      <Routes>
        <Route path="/payment/success" element={<PaymentResultPage />} /> {/* Payment success page */}
        <Route path="/payment/error" element={<PaymentResultPage />} /> {/* Payment error page */}
        
        {/* Google Calendar OAuth Callback */}
        <Route path="/calendar/callback" element={<CalendarCallbackPage />} />
        
        <Route path="/login" element={
          // Special case for new users in welcome form process
          (storeIsAuthenticated && user?.newUser && localStorage.getItem('showing_welcome_form') === 'true') ?
            <LoginPage onAuthComplete={handleAuthComplete} onThemeToggle={handleThemeToggle} theme={theme} /> :
            // Regular authenticated users go to dashboard
          storeIsAuthenticated ? 
          <Navigate to="/" replace /> : 
          <LoginPage onAuthComplete={handleAuthComplete} onThemeToggle={handleThemeToggle} theme={theme} />
        } />
        
        <Route path="/signup" element={
          storeIsAuthenticated ? 
          <Navigate to="/" replace /> : 
          <LoginPage onAuthComplete={handleAuthComplete} onThemeToggle={handleThemeToggle} theme={theme}/>
        } />
        
        <Route path="/dashboard" element={<DashboardPage />} />

        <Route path="/" element={
          storeIsAuthenticated ? 
            appState === 'setup-profile' ? 
              <Navigate to="/setup-profile" replace /> : 
              <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
                <DashboardNew />
              </ProtectedRoute>
          : <Navigate to="/login" replace />
        } />
        
        <Route path="/setup-profile" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <ResumeCareerSetupPage onComplete={handleProfileSetupComplete} fromSource={location.search.includes('source=login') ? "login" : "onboarding"} />
          </ProtectedRoute>
        } />
        
        <Route path="/profile-preferences" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <SettingsPage 
              subscriptionInfo={subscriptionInfo}
              isLoadingSubscription={isLoadingSubscription}
              onSubscriptionUpdate={refreshSubscriptionInfo}
            />
          </ProtectedRoute>
        } />
        
        {/* Interview Preview Page */}
        <Route path="/interview-preview/:id" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <InterviewPreviewPage userDetails={{
              id: safeUser.id,
              name: safeUser.name,
              profilePicture: safeUser.profilePicture
            }} />
          </ProtectedRoute>
        } />

        {/* Public Screening Interview Page */}
        <Route path="/screening/:id" element={<ScreeningInterviewPage />} />

        {/* Public Implementor Interview Pages (NO authentication required) */}
        <Route path="/screening-interview/:unique_id" element={<PublicInterviewPage />} />
        <Route path="/screening-interview-active/:id" element={<PublicInterviewActivePage />} />

        {/* New Interview Page */}
        <Route path="/interview/:id" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            {/* Add check for completed interview before rendering */}
            {(() => {
              // Get completed interview ID from sessionStorage
              const completedInterviewId = sessionStorage.getItem('completed_interview_id');
              const pathInterviewId = location.pathname.split('/interview/')[1];
              
              // If current interview ID matches completed ID, redirect to dashboard
              if (completedInterviewId && completedInterviewId === pathInterviewId) {
                // We can't directly use showToast here since it's not a React component
                // Instead, set a flag to show toast after navigation
                sessionStorage.setItem('show_completed_toast', 'true');
                return <Navigate to="/" replace />;
              }
              
              // Otherwise render the interview component
              return (
                <StableInterviewPage 
                  videoRef={videoRef} 
                  videoStream={videoStream} 
                  onInterviewComplete={handleInterviewComplete} 
                  onVideoStreamChange={setVideoStream} 
                  userData={{
                    name: safeUser.name,
                    profileImage: safeUser.profileImage,
                    interviewGoals: safeUser.interviewGoals,
                    minutesLeft: isWeekend ? 99 : subscriptionInfo.minutesLeft // IF Weekend, then no limits
                  }}
                />
              );
            })()}
          </ProtectedRoute>
        } />
        
        {/* Interview Feedback Page, Takes userInterviewId as Id in a parameter */}
        <Route path="/interview-feedback/:id" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <InterviewFeedbackPage />
          </ProtectedRoute>
        } />
        
        <Route path="/subscription/*" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <SubscriptionPage 
              fromSource="dashboard" 
              subscriptionInfo={subscriptionInfo}
              isLoadingSubscription={isLoadingSubscription}
              onSubscriptionUpdate={refreshSubscriptionInfo}
            />
          </ProtectedRoute>
        } />
        
        <Route path="/subscriptions/*" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <SubscriptionPage 
              fromSource="dashboard" 
              subscriptionInfo={subscriptionInfo}
              isLoadingSubscription={isLoadingSubscription}
              onSubscriptionUpdate={refreshSubscriptionInfo}
            />
          </ProtectedRoute>
        } />
        
        <Route path="/onboarding/subscription/*" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <SubscriptionPage 
              fromSource="onboarding" 
              subscriptionInfo={subscriptionInfo}
              isLoadingSubscription={isLoadingSubscription}
              onSubscriptionUpdate={refreshSubscriptionInfo}
            />
          </ProtectedRoute>
        } />
        
        <Route path="/my-interviews" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <InterviewHistoryPage />
          </ProtectedRoute>
        } />
        
        <Route path="/interview-series/:id" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <InterviewSeriesDetailsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/settings-preferences" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <SettingsPage 
              subscriptionInfo={subscriptionInfo}
              isLoadingSubscription={isLoadingSubscription}
              onSubscriptionUpdate={refreshSubscriptionInfo}
            />
          </ProtectedRoute>
        } />
        
        <Route path="/privacy" element={
          <PrivacyPolicy isAuthenticated={storeIsAuthenticated}/>
        } />
        
        <Route path="/terms" element={
          <TermsOfService isAuthenticated={storeIsAuthenticated}/>
        } />
        
        <Route path="/cancellation-refund" element={
          <CancellationRefundPolicy isAuthenticated={storeIsAuthenticated}/>
        } />
        
        {/* Modified catch-all route to check if path is subscriptions first */}
        <Route path="*" element={
          location.pathname.startsWith('/subscriptions') || location.pathname.startsWith('/subscription') ? 
          <Navigate to="/subscriptions" replace /> : 
          <Navigate to="/dashboard" replace />
        } />
        
        {/* Add the route for domain updates detail page */}
        <Route path="/domain-updates/:updateId" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <DomainUpdateDetailsPage />
          </ProtectedRoute>
        } />
        
        {/* Feed Page */}
        <Route path="/feed" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <FeedPage />
          </ProtectedRoute>
        } />
        
        {/* Browse Series Page */}
        <Route path="/browse-series" element={
          <ProtectedRoute isAuthenticated={storeIsAuthenticated}>
            <BrowseSeriesPage />
          </ProtectedRoute>
        } />
      </Routes>
      
      {/* Add the connection monitor for WebSocket debugging */}
      <ConnectionMonitor />
      
      {/* Add connection stability indicator */}
      <ConnectionStabilityIndicator position="bottom-right" />
    </div>
  );
}

function App() {
  return (
    <ResponsiveProvider>
      <ThemeProvider attribute="class" defaultTheme="light">
        <Router>
          <ScrollToTop />
          <AnalyticsProvider>
          <ToastProvider>
            <AuthProvider>
              <ModalProvider>
                <ModalContextProvider>
                    <Routes>
                      <Route path="/*" element={<MainApp />} />
                    </Routes>
                  </ModalContextProvider>
              </ModalProvider>
            </AuthProvider>
          </ToastProvider>
          </AnalyticsProvider>
        </Router>
      </ThemeProvider>
    </ResponsiveProvider>
  );
}

export default App;
